import { ThemeProvider } from 'styled-components';
import {
  LoggerProvider,
  MeetingProvider,
  darkTheme,
} from 'amazon-chime-sdk-component-library-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  MeetingContextProvider,
  MeetingPage as MeetingPageEnum,
  VOICE_ONLY_MEETING_TYPE,
  useMeetingContext,
} from '../../contexts/meeting/meetingContext';
import MeetingCallPageLayout from '../../components/layout/meeting';
import { useAuth } from '../../contexts/auth/authContext';
import { ROLE_VALUES } from '../../constants/customerConstant';
import meetingService from '../../services/meetingService';
import { isAxiosError } from 'axios';
import { ConsoleLogger, LogLevel } from 'amazon-chime-sdk-js';
import NotFoundPage from './notFoundPage';
import { useAppDispatch } from '../../hooks/global/typedReduxHooks';
import { setInterviewQuestions } from '../../slices/interviewPlannerSlice';
import interviewPlannerService from '~/services/interviewPlanner';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { useIntl } from 'react-intl';

export const GUEST_USER = 0;

function MeetingSetup() {
  const queryParams = new URLSearchParams(location.search);
  const code = queryParams.get('code');
  const name = queryParams.get('name');
  // The URLSearchParams constructor interprets plus signs ( + ) as spaces, which might cause problems
  const fixedCode = code?.replace(' ', '+') ?? null;
  const { createToastNotification } = useToastNotificationContext();
  const intl = useIntl();

  const [error, setError] = useState<{ code: string; message: string }>();
  const { userType } = useAuth();
  const { meetingID } = useParams<{ meetingID: string }>();
  const {
    joinMeeting,
    startMeeting,
    setMeetingPage,
    meetingDetails,
    setAttendees,
    mediaPipelineID,
    setIsVoiceOnlyCall,
  } = useMeetingContext();
  const dispatch = useAppDispatch();

  if (!meetingID) {
    throw new Error('Meeting ID is required');
  }

  useEffect(() => {
    (async () => {
      try {
        meetingDetails.current = await meetingService.fetchMeeting(meetingID, {
          code: fixedCode,
          name: name,
        });
        setIsVoiceOnlyCall(
          meetingDetails.current?.meeting_type === VOICE_ONLY_MEETING_TYPE,
        );
        setAttendees(
          await meetingService.fetchMeetingAttendee(meetingID, {
            code: fixedCode,
            name: name,
          }),
        );
      } catch (error) {
        if (isAxiosError(error)) {
          setError({
            code: error.response?.status.toString() ?? '',
            message: error.response?.data.detail ?? 'Something went wrong',
          });
        }
        return;
      }
      await joinMeeting();

      // If user is WB; GoTo Pre-video call page
      if (userType === ROLE_VALUES.WHISTLE_BLOWER || userType === GUEST_USER) {
        setMeetingPage(MeetingPageEnum.PRE_VIDEO_CALL);
      } else {
        await startMeeting();
        if (meetingDetails.current) {
          // Start recording
          meetingService
            .startRecording(
              meetingDetails.current.Meeting.MeetingId,
              meetingDetails.current.report_id,
              {
                code: fixedCode,
                token: meetingID,
              },
            )
            .then(
              (reponse?: {
                MediaCapturePipeline?: { MediaPipelineId?: string };
              }) => {
                if (reponse?.MediaCapturePipeline?.MediaPipelineId) {
                  mediaPipelineID.current =
                    reponse.MediaCapturePipeline.MediaPipelineId;
                }
              },
            );
          try {
            const data = await interviewPlannerService.fetchInterviewQuestions(
              meetingDetails.current.report_reference_id,
            );
            dispatch(setInterviewQuestions(data));
          } catch (err) {
            createToastNotification(
              intl.formatMessage({
                defaultMessage: 'Failed to fetch interview questions',
              }),
              'error',
            );
          }
        }
      }
    })();
  }, []);

  return (
    <>
      {error && (
        <NotFoundPage statusCode={error.code} statusMessage={error.message} />
      )}
      {!error && <MeetingCallPageLayout />}
    </>
  );
}

function MeetingCallPage() {
  return (
    <ThemeProvider theme={darkTheme}>
      <LoggerProvider logger={new ConsoleLogger('Chime Logs', LogLevel.OFF)}>
        <MeetingProvider>
          <MeetingContextProvider>
            <MeetingSetup />
          </MeetingContextProvider>
        </MeetingProvider>
      </LoggerProvider>
    </ThemeProvider>
  );
}

export default MeetingCallPage;
